import { Typography } from '@mui/material';
import { memo, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import AuthLayoutCenter from '@/components/Global/layouts/AuthLayout/AuthLayoutCenter';
import StandardDesktopFooter from '@/components/Global/layouts/StandardLayout/StandardDesktopLayout/StandardDesktopFooter';
import { FQ_BUTTON_VARIANTS } from '@/components/muiCustomized/FQButton/constant';
import FQButton from '@/components/muiCustomized/FQButton/FQButton';
import { MEDIA_QUERIES_BREAKPOINTS } from '@/globalStyles';
import { LINK } from '@/pages/Routing/routesAndLinks';
import { DEFAULT_GUEST_ROUTE } from '@/pages/Routing/Routing';
import FourArmsStar from '@/resources/image/four_arms_star.png';
import ManyArmsStar from '@/resources/image/many_arms_star.png';
import AppleButton from '@/resources/marketplaceButtons/primary_apple_button.svg';
import GoogleButton from '@/resources/marketplaceButtons/primary_google_button.svg';
import { formatUnicorn } from '@/utils/string';

export interface IAuthFeedbackViewProps {
    className?: string;
    title: string;
    subtitle: ReactNode;
    navigateTo?: string;
    ctaLabel: string;
    hideMarketplaceButtons?: boolean;
}

const AuthFeedbackView = ({
    className,
    title,
    subtitle,
    navigateTo,
    ctaLabel,
    hideMarketplaceButtons,
}: IAuthFeedbackViewProps) => {
    const navigate = useNavigate();

    return (
        <div className={className}>
            <AuthLayoutCenter hideFooter centerLogo>
                <Content>
                    <WelcomeTitle variant="large_title">{title}</WelcomeTitle>
                    <SuccessText variant="regular_text" align="center">
                        {subtitle}
                    </SuccessText>
                    <ButtonsRow>
                        <FQButton
                            data-variant={FQ_BUTTON_VARIANTS.fqPrimary}
                            color="fqBlack"
                            onClick={() => navigate(navigateTo ?? DEFAULT_GUEST_ROUTE)}
                        >
                            <Typography variant="button">{ctaLabel}</Typography>
                        </FQButton>
                        {!hideMarketplaceButtons && (
                            <>
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={formatUnicorn(LINK.APP_APPLE_STORE, { lang: navigator.language })}
                                >
                                    <img src={AppleButton} alt="App Store" height={40} />
                                </a>
                                <a target="_blank" rel="noopener noreferrer" href={LINK.APP_GOOGLE_STORE}>
                                    <img src={GoogleButton} alt="Google Play" height={40} />
                                </a>
                            </>
                        )}
                    </ButtonsRow>
                </Content>
            </AuthLayoutCenter>
            <ManyArmsStarImage src={ManyArmsStar} alt="" aria-hidden="true" />
            <FourArmsStarsWrapper>
                <img src={FourArmsStar} alt="" aria-hidden="true" />
                <img src={FourArmsStar} alt="" aria-hidden="true" />
            </FourArmsStarsWrapper>
        </div>
    );
};

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2.5rem;
    text-align: center;

    @media screen and (width < ${MEDIA_QUERIES_BREAKPOINTS.stablet}px) {
        gap: 1.5rem;
    }
`;

const ButtonsRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    justify-content: center;

    a {
        display: flex;
        align-items: center;
    }

    @media screen and (width < ${MEDIA_QUERIES_BREAKPOINTS.stablet}px) {
        img {
            height: 32px;
        }
    }
`;

const WelcomeTitle = styled(Typography)`
    && {
        @media screen and (width < ${MEDIA_QUERIES_BREAKPOINTS.stablet}px) {
            font-size: 2rem;
        }
    }
`;

const SuccessText = styled(Typography)`
    && {
        font-size: 1.25rem;

        @media screen and (width < ${MEDIA_QUERIES_BREAKPOINTS.stablet}px) {
            font-size: 1rem;
        }
    }
`;

const ManyArmsStarImage = styled.img`
    position: absolute;
    top: 3rem;
    right: 10rem;
    width: 12rem;
    pointer-events: none;

    @media screen and (width < ${MEDIA_QUERIES_BREAKPOINTS.laptop}px) {
        right: 3vw;
        width: 9rem;
    }

    @media screen and (width < ${MEDIA_QUERIES_BREAKPOINTS.stablet}px) {
        right: 2vw;
        width: 6rem;
    }
`;

const FourArmsStarsWrapper = styled.div`
    position: absolute;
    bottom: 8rem;
    left: 10rem;
    display: flex;
    align-items: flex-end;
    gap: 0.25rem;
    pointer-events: none;

    img:first-child {
        width: 3.75rem;
    }

    img:last-child {
        width: 2.25rem;
    }

    @media screen and (width < ${MEDIA_QUERIES_BREAKPOINTS.laptop}px) {
        left: 3vw;

        img:first-child {
            width: 2.75rem;
        }

        img:last-child {
            width: 1.75rem;
        }
    }

    @media screen and (width < ${MEDIA_QUERIES_BREAKPOINTS.stablet}px) {
        left: 2vw;

        img:first-child {
            width: 2rem;
        }

        img:last-child {
            width: 1.25rem;
        }
    }
`;

const Memoized = memo(AuthFeedbackView);
const Styled = styled(Memoized)`
    position: relative;
    overflow: hidden;

    ${StandardDesktopFooter} {
        align-self: stretch;
    }
`;

export default Styled;
